import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box } from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Button, Spin } from "antd";
import { useSelector } from "react-redux";
import { Link, Navigate } from "react-router-dom";
import NewMiniDrawer from "../../components/navMenu/menu-nav";
import freightApi2 from "../../service/modules/freight.api";
import {
  CARRIER_APPLICATIONS_PAGE_SIZE,
  formatApplicationDate,
  formatCurrency,
  formatWaitingSince,
  getApiErrorMessage,
  mapCarrierApplicationItem,
  parseCarrierApplicationsResponse,
} from "./carrierApplications.mapper";
import "./minhas-candidaturas.css";

const FREIGHT_LIST_ROUTE = "/freightlistCopy";
const TAB_WAITING = "waiting";
const TAB_ACCEPTED = "accepted";

function ApplicationCard({ application, variant }) {
  const waitingSince = application.isWaiting
    ? formatWaitingSince(application.registeredAt)
    : null;
  const isAccepted = variant === TAB_ACCEPTED;

  return (
    <article
      className={`candidaturas-card ${
        isAccepted ? "candidaturas-card--accepted" : "candidaturas-card--waiting"
      }`}
    >
      <div
        className={`candidaturas-card__strip ${
          isAccepted
            ? "candidaturas-card__strip--accepted"
            : "candidaturas-card__strip--waiting"
        }`}
      >
        {application.applicationStatusLabel}
      </div>

      <div className="candidaturas-card__body">
        <div className="candidaturas-card__header">
          <div className="candidaturas-card__number">#{application.number}</div>
          <div className="candidaturas-card__shipper">{application.shipperName}</div>
          <div className="candidaturas-card__value">
            {formatCurrency(application.fixedPriceAmount)}
          </div>
        </div>

        <div className="candidaturas-card__route">
          <LocalShippingIcon className="localShippingIcon" />
          <span>{application.origin}</span>
          <ArrowForwardIcon sx={{ fontSize: 16, color: "rgba(1,36,66,0.35)" }} />
          <span>{application.destination}</span>
        </div>

        <div className="candidaturas-card__meta">
          <div className="candidaturas-card__meta-row">
            <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: "#ff3100" }} />
            <span>Coleta: {formatApplicationDate(application.collectionDate)}</span>
          </div>
          {application.freightStatusDescribe ? (
            <div className="candidaturas-card__meta-row">
              <span>Status do frete: {application.freightStatusDescribe}</span>
            </div>
          ) : null}
          {isAccepted && application.registeredAt ? (
            <div className="candidaturas-card__meta-row">
              <span>
                Candidatura em {formatApplicationDate(application.registeredAt)}
              </span>
            </div>
          ) : null}
        </div>

        <div className="candidaturas-card__footer">
          {waitingSince ? (
            <span className="candidaturas-card__waiting">Na fila {waitingSince}</span>
          ) : (
            <span />
          )}

          {application.canGoToFreightList ? (
            <Link to={FREIGHT_LIST_ROUTE} style={{ textDecoration: "none" }}>
              <Button
                type="primary"
                size="small"
                className="candidaturas-btn-primary"
                icon={<OpenInNewOutlinedIcon style={{ fontSize: 16 }} />}
              >
                Ir para listagem de fretes
              </Button>
            </Link>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function EmptyTabState({ tab, hasCarrier }) {
  if (!hasCarrier) return null;

  const isWaiting = tab === TAB_WAITING;

  return (
    <Box className="candidaturas-empty">
      <div className="candidaturas-empty__title">
        {isWaiting ? "Nenhum frete em análise" : "Nenhum frete aceito ainda"}
      </div>
      <p style={{ margin: "0 0 16px", maxWidth: 420, marginInline: "auto" }}>
        {isWaiting
          ? "Quando você entrar na fila de um frete em Lances, ele aparecerá aqui até o embarcador analisar sua candidatura."
          : "Depois que o embarcador aceitar sua transportadora, o frete ficará disponível nesta aba e na listagem principal."}
      </p>
      {isWaiting ? (
        <Link to="/lances">
          <Button type="primary" className="candidaturas-btn-primary" id="ButtonHover">
            Ver fretes em Lances
          </Button>
        </Link>
      ) : (
        <Link to={FREIGHT_LIST_ROUTE}>
          <Button className="candidaturas-btn-outline">Abrir listagem de fretes</Button>
        </Link>
      )}
    </Box>
  );
}

export default function MinhasCandidaturasPage() {
  const isLoggedIn = useSelector((state) => state.usuarioLogado) > 0;
  const user = useSelector((state) => state.user);

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [activeTab, setActiveTab] = useState(TAB_WAITING);

  const canView =
    user?.permissoes?.freightCarrierApplications?.isView || user?.viewALL;

  const idCarrier = user?.uidShipper;

  const waitingApplications = useMemo(
    () => applications.filter((item) => item.isWaiting),
    [applications],
  );

  const acceptedApplications = useMemo(
    () => applications.filter((item) => item.isAccepted),
    [applications],
  );

  const visibleApplications =
    activeTab === TAB_ACCEPTED ? acceptedApplications : waitingApplications;

  const loadApplications = useCallback(
    async ({ cursor, append = false } = {}) => {
      if (!idCarrier) {
        setApplications([]);
        setNextCursor(null);
        setHasMore(false);
        return;
      }

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError("");

      try {
        const params = {
          idCarrier,
          limit: CARRIER_APPLICATIONS_PAGE_SIZE,
        };
        if (cursor) params.cursor = cursor;

        const response = await freightApi2.getCarrierApplications(params);
        const parsed = parseCarrierApplicationsResponse(response, cursor);
        const mapped = parsed.items.map(mapCarrierApplicationItem);

        setApplications((prev) => (append ? [...prev, ...mapped] : mapped));
        setNextCursor(parsed.nextCursor);
        setHasMore(parsed.hasMore);
      } catch (err) {
        console.error("[MinhasCandidaturas] Erro ao carregar candidaturas:", err);
        setError(
          getApiErrorMessage(
            err,
            "Não foi possível carregar suas candidaturas.",
          ),
        );
        if (!append) {
          setApplications([]);
          setNextCursor(null);
          setHasMore(false);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [idCarrier],
  );

  useEffect(() => {
    if (isLoggedIn && canView) {
      loadApplications();
    }
  }, [isLoggedIn, canView, loadApplications]);

  const handleRefresh = () => {
    loadApplications();
  };

  const handleLoadMore = () => {
    if (!hasMore || !nextCursor || loadingMore) return;
    loadApplications({ cursor: nextCursor, append: true });
  };

  if (!isLoggedIn) {
    return <Navigate to="/login" />;
  }

  if (!canView) {
    return <Navigate to="/" />;
  }

  return (
    <NewMiniDrawer
      divOpen={
        <div className="freight-content candidaturas-page">
          <div className="candidaturas-page__toolbar">
            <div>
              <h3>Meus lances</h3>
              <p className="candidaturas-page__subtitle">
                Acompanhe os fretes em que sua transportadora está na fila ou já foi
                aceita pelo embarcador.
              </p>
            </div>
            <Button
              type="primary"
              id="ButtonHover"
              icon={<RefreshOutlinedIcon style={{ fontSize: 16 }} />}
              onClick={handleRefresh}
              loading={loading}
              disabled={!idCarrier}
            >
              Atualizar
            </Button>
          </div>

          <div className="candidaturas-summary">
            <div className="candidaturas-summary__card candidaturas-summary__card--navy">
              <div className="candidaturas-summary__label">Em análise</div>
              <div className="candidaturas-summary__value">{waitingApplications.length}</div>
            </div>
            <div className="candidaturas-summary__card candidaturas-summary__card--accent">
              <div className="candidaturas-summary__label">Aceitas</div>
              <div className="candidaturas-summary__value">{acceptedApplications.length}</div>
            </div>
          </div>

          {!idCarrier ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Usuário sem transportadora vinculada. Não é possível listar candidaturas.
            </Alert>
          ) : null}

          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <div className="candidaturas-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === TAB_WAITING}
              className={`candidaturas-tab ${
                activeTab === TAB_WAITING ? "candidaturas-tab--active" : ""
              }`}
              onClick={() => setActiveTab(TAB_WAITING)}
            >
              <HourglassEmptyOutlinedIcon sx={{ fontSize: 18 }} />
              Em análise
              <span className="candidaturas-tab__count">{waitingApplications.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === TAB_ACCEPTED}
              className={`candidaturas-tab ${
                activeTab === TAB_ACCEPTED ? "candidaturas-tab--active" : ""
              }`}
              onClick={() => setActiveTab(TAB_ACCEPTED)}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
              Aceitas
              <span className="candidaturas-tab__count">{acceptedApplications.length}</span>
            </button>
          </div>

          {loading && applications.length === 0 ? (
            <div className="candidaturas-loading">
              <Spin size="large" />
            </div>
          ) : null}

          {!loading && visibleApplications.length === 0 ? (
            <EmptyTabState tab={activeTab} hasCarrier={Boolean(idCarrier)} />
          ) : null}

          {visibleApplications.length > 0 ? (
            <div className="candidaturas-list">
              {visibleApplications.map((application) => (
                <ApplicationCard
                  key={`${application.freightId}_${application.applicationStatus}`}
                  application={application}
                  variant={activeTab}
                />
              ))}
            </div>
          ) : null}

          {hasMore && visibleApplications.length > 0 ? (
            <div className="candidaturas-load-more">
              <Button
                className="candidaturas-btn-outline"
                onClick={handleLoadMore}
                loading={loadingMore}
              >
                Carregar mais
              </Button>
            </div>
          ) : null}
        </div>
      }
    />
  );
}
